import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useExcDtcFilesV1FileQueryServiceFileGet } from '@interxfi/api-client/generated/hooks/exc-dtc-files-v1-file-query-service-file-get';
import { EntityActionMenu, type EntityActionMenuConfig } from '@interxfi/app-components/components/entity-action-menu/entity-action-menu';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  LAYOUT_ZERO_CONTAINER_PADDING,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list';
import { Typography } from '@interxfi/design-system/components/typography';
import type { ExcDtcFilesV1FileState } from '@interxfi/types';
import { DtcFileEventDrawer, DtcFileEventSummary } from '../../../../../../../../../../../features/dtc/files/v1/dtc-file-event';
import { getDtcFileStateListItems } from '../../../../../../../../../../../features/dtc/files/v1/dtc-file-list-items';
import { getDtcFileDetailPath } from '../../../../../../../../../../../features/dtc/files/v1/dtc-file-routing';
import { DtcFileStatusPill } from '../../../../../../../../../../../features/dtc/files/v1/dtc-file-status-pill';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/_clearing-required/clearing/vendor/_vendor/dtcc/_dtcc/file/_file/$fileId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `dtc-file-${params.fileId}`,
        label: 'DTC File',
        to: getDtcFileDetailPath(params.fileId),
        suffix: <CopyButton content={params.fileId} label="Copy DTC File ID" tooltip={{ copy: params.fileId, copied: 'Copied DTC File ID' }} />,
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('DTC File'),
  component: DtcFileDetail,
});

function getListItems(data: ExcDtcFilesV1FileState | undefined) {
  const listItems = getDtcFileStateListItems(data);

  return {
    generalLeft: [listItems.data.spec.path] satisfies ReadonlyListItem[],
    generalRight: [listItems.fileId, listItems.data.spec.fileRequestId] satisfies ReadonlyListItem[],
  };
}

function getMenuItems(fileId: string): EntityActionMenuConfig {
  return {
    eventDrawer: {
      type: 'drawer',
      drawer: {
        Component: DtcFileEventDrawer,
        props: { request: { fileId } },
      },
    },
    actions: undefined,
  };
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load DTC file', 'view this DTC file');

function DtcFileDetail() {
  const { fileId } = Route.useParams();
  const request = { fileId } as const;
  const { data, error, isPending } = useExcDtcFilesV1FileQueryServiceFileGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.file), [data?.file]);
  const menuItems = useMemo(() => getMenuItems(fileId), [fileId]);

  return (
    <PageLayout gap={DEFAULT_PAGE_LAYOUT_GAP} maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section>
        <VerticalStack gap={7}>
          <PageHeader>
            <HorizontalStack gap={2} wrap="nowrap">
              <DtcFileStatusPill isPending={showPending} size="large" status={data?.file?.status} />
            </HorizontalStack>
            <EntityActionMenu {...menuItems} isPending={showPending} />
          </PageHeader>

          <VerticalStack gap={4}>
            <Typography.Level3>Events</Typography.Level3>

            <DtcFileEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
          </VerticalStack>
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyList isPending={showPending} items={listItems.generalLeft} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.generalRight} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
